import { Injectable } from '@angular/core';
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {environment} from "../../environments/environment";
import {Profile, ReqProfileSave} from "../models/profile.model";
import {
  CatalogByOwner,
  CatalogCategories,
  CatalogFind,
  CatalogFindReq,
  CatalogItem,
  CatalogItemSchedule
} from "../models/catalog.model";
import {Media} from "../models/media.model";
import {
  Product,
  ProductList, ProductListCategory,
  ProductSaveReq,
  ProductSchedule, ProductStatus,
  ProductTrash
} from "../models/ent-product.model";
import {CatalogProportion} from "../models/catalog-propoptions.model";
import {LoadingService} from "./loading.service";
import {RentCreateReq, RentInitReq, RentListReq} from "../models/rent.model";
import {Page} from "../models/page.model";
import {ChatMessages, ChatMessagesReq, ChatNewMessageReq} from "../models/chat.model";
import {FeedbackCreateReq, FeedbackListReq} from "../models/feedback.model";
import { AppVersionService } from './app-version.service';

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private apiUrl = environment.apiUrl;

  constructor(
    private readonly http: HttpClient,
    private readonly appVersion: AppVersionService,
    private readonly loadingService: LoadingService
  ) { }

  public readonly catalog = ((api: this) => ({
    // Получить категории.
    catalogCategories(isExtraDataLoad: boolean = true): Promise<CatalogCategories[]> { return api.apiCallPost('v1/catalog-categories', { isExtraDataLoad: isExtraDataLoad }); },
    // Найти объявление по параметрам.
    catalogFind(req: any): Promise<CatalogFind> { return api.apiCallPost('v1/catalog-find', req); },
    // Просмотр объявления.
    catalogItem(id: string, isLoading: boolean = false): Promise<any> { return api.apiCallPost('v1/catalog-item', { id: id }, false, isLoading); },
    // Просмотр клендаря аренд по объявлению.
    catalogItemSchedule(id: string, from: Date, to: Date): Promise<any> { return api.apiCallPost('v1/catalog-item-schedule', { id: id, from: from, to: to }); },
    // Просмотр всех активных объявлений пользователя.
    catalogByOwner(id: string): Promise<CatalogByOwner[]> { return api.apiCallPost('v1/catalog-by-owner', { id: id }); },
    // Получить справочные значения.
    proportions(): Promise<CatalogProportion[]> { return api.apiCallPost('v1/catalog-propoptions', {}); },
    // Установить/снять признак 'Избранное'.
    setFavorite(id: string, isFavorite: boolean): Promise<any> { return api.apiCallPost('v1/catalog-item-favorite', { id: id, isFavorite: isFavorite }); },
    // Получить избранные объявления.
    catalogFavorites(): Promise<any[]> { return api.apiCallPost('v1/catalog-favorites', {}); },
  }))(this);

  public readonly entProduct = ((api: this) => ({
    // Получить список своих объявлений.
    list(status: ProductListCategory, page: any): Promise<any> { return api.apiCallPost('v1/product-list', { status: status, page: page }); },
    // Получить полные данные объявления.
    get(id: string): Promise<any> { return api.apiCallPost('v1/product-get', { id: id }); },
    // Получить данные объявления.
    info(id: string): Promise<any> { return api.apiCallPost('v1/product-info', { id: id }); },
    // Сохранить данные объвление.
    save(req: ProductSaveReq): Promise<any> { return api.apiCallPost('v1/product-save', req); },
    // Сохранить/изменить статус объявления.
    publish(id: string): Promise<any> { return api.apiCallPost('v1/product-publish', { id: id }); },
    // Удалить объвление.
    remove(id: string, status: ProductStatus): Promise<ProductTrash> { return api.apiCallPost('v1/product-remove', { id: id, status: status }); },
    // Удалить медиа-файл
    mediaRemove(productId: string, mediaId: string): Promise<string[]> { return api.apiCallPost('v1/product-media-remove', { productId, mediaId }); },
    // Получить список характеристик допустимых для категории.
    propertyList(id: string, isLoading: boolean = false): Promise<any[]> { return api.apiCallPost('v1/product/property-list', { id: id }, false, isLoading); },
  }))(this);

  public readonly profile = ((api: this) => ({
    // Получить данные собственного профиля.
    get(isLoading: boolean = true): Promise<Profile> { return api.apiCallPost('v1/profile', {}, false, isLoading); },
    // Сохранить отредактированные данные собственного профиля.
    save(req: ReqProfileSave): Promise<Profile> { return api.apiCallPost('v1/profile-save', req); },
    // Задать новый номер телефона.
    phoneChange(phoneNumber: string): Promise<Profile> { return api.apiCallPost('v1/profile-phone-change', { phoneNumber: phoneNumber }); },
    // Подтвердить новый номер телефона.
    phoneConfirm(code: string): Promise<Profile> { return api.apiCallPost('v1/profile-phone-confirm', { code: code }); },
    // Удалить аккаунт.
    delete(): Promise<any> { return api.apiCallPost('v1/profile-delete', {}); },
    // Загрузить фото для аватара.
    photoUpload(formData: any): Promise<Profile> { return api.apiCallPost('v1/profile-photo-upload', formData, true); },
    // Получить данные профиля пользователя.
    profileInfo(id: string): Promise<any> { return api.apiCallPost('v1/profile-info', { id: id }); },
    // Сменить пароль.
    passwordChange(newPassword: string, isLoading: boolean = false): Promise<any> { return api.apiCallPost('v1/profile-password-change', { newPassword }, false, isLoading); },
  }))(this);

  public readonly entRent = ((api: this) => ({
    // Список аренд по объявлению.
    rentList(req: RentListReq): Promise<any> { return api.apiCallPost('v1/rent-list', req); },
    // Получить объект аренды.
    rentGet(id: string): Promise<any> { return api.apiCallPost('v1/rent-get', { id: id }, false, true); },
    // Подготовить данные аренды/бронирования.
    rentInit(req: RentInitReq): Promise<any> { return api.apiCallPost('v1/rent-init', req, false, true); },
    // Создать аренду/бронирование.
    rentCreate(req: RentCreateReq): Promise<any> { return api.apiCallPost('v1/rent-create', req, false, true); },
    // Просмотр клендаря аренд по своим объявлениям.
    schedule(from: Date, to: Date, productId: string | null): Promise<any[]> { return api.apiCallPost('v1/rent-schedule', { from: from, to: to, productId: productId }, false, true); },
    // Отменить аренду/бронирование.
    rentCancel(id: string): Promise<any> { return api.apiCallPost('v1/rent-cancel', { id: id }, false, true); },
    // Отметить аренду оплаченной.
    rentPaid(id: string): Promise<any> { return api.apiCallPost('v1/rent-paid', { id: id }, false, true); },
    // Отметить аренду закрытой.
    rentClose(id: string): Promise<any> { return api.apiCallPost('v1/rent-close', { id: id }, false, true); },
    // Список последних оплаченных аренд.
    rentListShortsPaid(productId: string, page: Page): Promise<any> { return api.apiCallPost('v1/rent-list-shorts-paid', { productId: productId, page: page }, false, false); },
    // Список последних окрытых аренд.
    rentListShorts(productId: string, page: Page): Promise<any> { return api.apiCallPost('v1/rent-list-shorts', { productId: productId, page: page }, false, false); },
    // Список арендаторов.
    rentListRenters(productId: string, page: Page): Promise<any> { return api.apiCallPost('v1/rent-list-renters', { productId: productId, page: page }, false, false); },
    // Открыть спор.
    rentAppeal(rentId: string, reason: string): Promise<any> { return api.apiCallPost('v1/rent-appeal', { rentId: rentId, reason: reason }, false, true); },
  }))(this);

  public readonly chat = ((api: this) => ({
    // Список чатов.
    list(req: Page, isLoading: boolean = false): Promise<any> { return api.apiCallPost('v1/chat-list', req, false, isLoading); },
    // Открыть чат.
    get(id: string, isLoading: boolean = false): Promise<any> { return api.apiCallPost('v1/chat-get', { id: id }, false, isLoading); },
    // Перейти к чату по объвлению.
    req(productId: string, isLoading: boolean = false): Promise<any> { return api.apiCallPost('v1/chat-req', { productId: productId }, false, isLoading); },
    // Сообщения чата.
    messages(req: ChatMessagesReq, isLoading: boolean = false): Promise<ChatMessages> { return api.apiCallPost('v1/chat-messages', req, false, isLoading); },
    // Написать сообщение.
    newMessage(req: any, isLoading: boolean = false): Promise<any> { return api.apiCallPost('v1/chat-new-message', req, true, isLoading); },
    // Количество не прочитанных сообщений.
    chatUnreadCount(): Promise<any> { return api.apiCallPost('v1/chat-unread-count', {}, false, false); },
    // Отметить дату последнего прочитанного сообщения..
    chatReadMessage(chatId: string, reatAt: Date): Promise<any> { return api.apiCallPost('v1/chat-read-messages', { chatId: chatId, reatAt: reatAt }, false, false); },
    // Выйти из чата.
    leave(id: string, isLoading: boolean = false): Promise<any> { return api.apiCallPost('v1/chat-leave', { id: id }, false, isLoading); },
  }))(this);

  public readonly feedback = ((api: this) => ({
    // Список отзывов.
    list(req: FeedbackListReq): Promise<any> { return api.apiCallPost('v1/rate-list', req, false, true); },
    // Подготовить данные для создание отзыва.
    init(rentId: string): Promise<any> { return api.apiCallPost('v1/rate-init', { rentId: rentId }, false, false); },
    // Создать отзыв.
    create(req: FeedbackCreateReq): Promise<any> { return api.apiCallPost('v1/rate-create', req, false, true); },
  }))(this);

  public readonly payment = ((api: this) => ({
    // Инициализация платежа.
    init(rentId: string): Promise<any> { return api.apiCallPost('v1/payment-init', { rentId: rentId }, false, false); },
    // Регистрация платежа.
    start(rentId: string, amount: number, entityId?: string): Promise<any> { return api.apiCallPost('v1/payment-start', { rentId: rentId, amount: amount, entityId }, false, true); },
  }))(this);

  public readonly media = ((api: this) => ({
    // Загрузить медиа-файлы для объявления.
    upload(formData: any): Promise<Media[]> { return api.apiCallPost('v1/media/product/upload', formData, true, true); },// Загрузить медиа-файлы для объявления.
    uploadFeedback(formData: any): Promise<Media[]> { return api.apiCallPost('v1/media/rate/upload', formData, true, true); },
  }))(this);

  public readonly notification = ((api: this) => ({
    // Добавить данные подписки.
    savePushSubscriber(subscription: string | undefined): Promise<any> { return api.apiCallPost('v1/notification-savePushSubscriber', { subscription }); },
    // Удалить данные подписки.
    removePushSubscriber(subscription: string | undefined): Promise<any> { return api.apiCallPost('v1/notification-removePushSubscriber', { subscription }); },
  }))(this);

  private async apiCallPost(url: string, body?: any, isFile: boolean = false, isLoading: boolean = false): Promise<any> {
    if (isLoading) { await this.loadingService.show(); }
    if (!isFile) {
      return this.http.post(this.apiUrl + url, body).toPromise().catch(e => { console.error(e) }).finally(async () => {
        if (isLoading) { await this.loadingService.hide(); }
      });
    } else {
      let headers = new HttpHeaders();
      headers.append('Content-Type', 'multipart/form-data');
      headers.append('Accept', 'text/plain');
      headers.append('X-AppVersion', this.appVersion.appVersion);
      return this.http.post(this.apiUrl + url, body, {
        headers: headers
      }).toPromise().catch(e => { console.error(e) }).finally(async () => {
        if (isLoading) { await this.loadingService.hide(); }
      });
    }
  }
  private apiCallGet(url: string, params?: any): Promise<any> {
    const options = {
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
      'Access-Control-Allow-Methods': 'GET, POST, OPTIONS, PUT, PATCH, DELETE',
      'Access-Control-Allow-Credentials': true,
      'Access-Control-Allow-Headers': 'X-Requested-With,content-type'
    }  as any;
    params ? options.params = params : null
    return this.http
      .get(this.apiUrl + url, options)
      .toPromise()
      .catch((e) => {
        console.error(e);
      });
  }
}
